import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import Highcharts, {
    Chart,
    Options,
    PointOptionsObject,
    SeriesAreasplineOptions,
    SeriesClickEventObject,
} from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';

@Component({
    selector: 'vc-area-spline-chart',
    imports: [HighchartsChartComponent],
    templateUrl: './area-spline-chart.component.html',
    styleUrl: './area-spline-chart.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AreaSplineChartComponent {
    highcharts: typeof Highcharts = Highcharts;

    private chartInstance?: Chart;

    options: Options = {
        chart: {
            type: 'areaspline',
            borderWidth: 0,
            spacingTop: 22,
        },
        title: {
            text: '',
        },
        credits: {
            enabled: false,
        },
        legend: {
            enabled: false,
        },
        exporting: {
            enabled: false,
        },
        xAxis: {
            title: {
                text: ``,
            },
            categories: [],
            minPadding: 0,
            tickmarkPlacement: 'on',
        },
        plotOptions: {
            areaspline: {
                pointPlacement: 'on',
            },
        },
        yAxis: [
            {
                title: {
                    text: ``,
                },
                labels: {
                    format: '{value}',
                },
            },
        ],
        series: [
            {
                marker: {
                    enabled: false,
                },
                data: [],
                name: '',
                color: 'var(--primary-200)',
                type: 'areaspline',
            },
        ],
        tooltip: {
            format: '<span>{point.category}: <b>{point.y}</b></span>',
        },
    };

    @Input()
    loading: boolean = false;

    @Input()
    height: string = '100%';

    @Input()
    set title(value: string) {
        this.options = { ...this.options, title: { ...this.options.title, text: value } };
        this._updateChart();
    }

    @Input()
    set data(value: Array<number | [string, number | null] | null | PointOptionsObject>) {
        this.options = {
            ...this.options,
            series: this.options.series?.[0]
                ? ([{ ...this.options.series[0], data: value }] as SeriesAreasplineOptions[])
                : [{ data: value }],
        };
        this._updateChart();
    }

    @Input()
    set categories(value: Array<string>) {
        this.options = {
            ...this.options,
            xAxis: {
                ...(this.options.xAxis as object),
                categories: value,
            },
        };
        this._updateChart();
    }

    @Input()
    set pointClick(value: (event: SeriesClickEventObject) => void) {
        this.options = {
            ...this.options,
            plotOptions: {
                ...(this.options.plotOptions ?? {}),
                series: {
                    ...(this.options.plotOptions?.series ?? {}),
                    point: {
                        ...this.options.plotOptions?.series?.point,
                        events: {
                            click: value,
                        },
                    },
                },
            },
        };
        this._updateChart();
    }

    @Input()
    set tooltipFormat(value: string) {
        this.options = {
            ...this.options,
            tooltip: {
                ...this.options.tooltip,
                format: value,
            },
        };
        this._updateChart();
    }

    onChartInstance(chart: Chart): void {
        this.chartInstance = chart;
        this._updateChart();
    }

    private _updateChart(): void {
        if (!this.chartInstance?.renderer) {
            return;
        }

        this.chartInstance.update(this.options, true, true);
    }
}
